import React from 'react';

import { SimpleTooltip } from '@eluve/components';
import {
  Badge,
  Button,
  Heading,
  Icon,
  Stack,
  Text,
  cn,
} from '@eluve/components';
import { billingCodeSchema } from '@eluve/llm-outputs';

import { OutputParsingError } from './OutputParsingError';
import { OutputTitle } from './OutputTitle';

export interface BillingCodeOutputProps {
  content: unknown;
}

export const BillingCodeOutput: React.FC<BillingCodeOutputProps> = ({
  content,
}) => {
  const billingCodes = billingCodeSchema.safeParse(content);

  if (!billingCodes.success) {
    return <OutputParsingError />;
  }

  return (
    <div className="space-y-4">
      <OutputTitle title="Billing Code Recommendations" />
      {billingCodes.data.map(
        ({ code, description, justification, type, excerpt }) => (
          <div className="space-y-2 border p-4 shadow-md" key={code}>
            <Stack
              direction="horizontal"
              align="start"
              justify="between"
              gap={6}
            >
              <Stack
                direction="horizontal"
                align="center"
                gap={2}
                fullWidth={false}
              >
                <Badge
                  className={cn(
                    type === 'CPT' && 'bg-bg-teal-strong',
                    type === 'ICD-10' && 'bg-bg-lightblue-strong',
                    type !== 'CPT' && type !== 'ICD-10' && 'bg-bg-gray-strong',
                  )}
                >
                  {type}
                </Badge>
                <Heading size="2xl" weight="semibold">
                  {code}
                </Heading>
                {type === 'ICD-10' && (
                  <a
                    href={`https://icd.who.int/browse10/2019/en#${code}`}
                    target="_blank"
                    className="underline"
                    rel="noreferrer"
                  >
                    <Icon name="ExternalLink" />
                  </a>
                )}
              </Stack>
              <Stack
                direction="horizontal"
                align="center"
                gap={1}
                fullWidth={false}
              >
                <SimpleTooltip label="Edit this diagnosis">
                  <Button variant="outline" size="sm">
                    <Icon name="Pencil" size="lg" />
                  </Button>
                </SimpleTooltip>

                <SimpleTooltip label="Remove this diagnosis">
                  <Button variant="outline" size="sm">
                    <Icon name="Trash" size="lg" />
                  </Button>
                </SimpleTooltip>
              </Stack>
            </Stack>
            <Text weight="regular" className="italic">
              {description}
            </Text>
            <Text className="mt-2">
              <span className="font-extrabold">Justification: </span>
              {justification}
            </Text>
            <Text className="mt-2">
              <span className="font-extrabold">Excerpt: </span>
              {excerpt}
            </Text>
          </div>
        ),
      )}
    </div>
  );
};
